import { useCallback, useState } from "react";
import { updatePaymentConfiguration } from "../services/payments";
import { useUserState } from "../state/user";
import { PaymentConfiguration } from "../types/payments";

/**
 * Patches a payment configuration. Used for the tax authority credentials and
 * for flagging the KYC documents as ready — the two remaining requirements
 * after the configuration itself is created.
 */
export default function useUpdatePaymentConfiguration() {
  const { token } = useUserState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const update = useCallback(
    async (
      configId: string,
      patch: Partial<Omit<PaymentConfiguration, "id">>
    ) => {
      if (!token) throw new Error("Missing access token");
      try {
        setLoading(true);
        setError(null);
        return await updatePaymentConfiguration({
          authToken: token,
          configId,
          patch
        });
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  /** Stores the Portal das Finanças credentials, needed to issue invoices. */
  const updateTaxAuthorityCredentials = useCallback(
    (configId: string, username: string, password: string) =>
      update(configId, { AT: { username, password } }),
    [update]
  );

  /**
   * Tells the backend every required KYC document is uploaded, which triggers
   * the submission to Easypay. Only call this once the document matrix for the
   * company type is complete.
   */
  const markDocumentsReady = useCallback(
    (configId: string) => update(configId, { documentsReady: true }),
    [update]
  );

  return {
    update,
    updateTaxAuthorityCredentials,
    markDocumentsReady,
    loading,
    error
  };
}
